import React, { HTMLProps, useContext, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import { IconElements } from '../../../config/icons';
import { reverseFormatNumber } from '../../../utils/parsers';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';
import { Account, AccountType, PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { YesNoFormGroup } from '../../general/form-inputs/YesNoFormGroup';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { BackButton, NextButton } from './NavigationButtons';
import { RemoveAccountModal } from '../supplement-overlay/RemoveAccountModal';
import { SupplementGeneralModal } from '../supplement-overlay/SupplementGeneralModal';
import { SavingButtons } from './DialogueDataSaver';

export type AccountReturnVoid = (account: Account) => void;

export const SupplementSection: React.FC<{isPartnerSection?: boolean}> = ({ isPartnerSection=false }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo, personalInfo },
        actions,
    } = useContext(PensionContext);

    return (
        <CocktailWrapperScreen 
            page={4} 
            title={isPartnerSection 
                ? t('pension-cocktail-fill-screen-supplement-partner-title')
                : t('pension-cocktail-fill-screen-supplement-title')} 
            subTitle={isPartnerSection
                ? t('pension-cocktail-fill-screen-supplement-partner-subtitle')
                : t('pension-cocktail-fill-screen-supplement-subtitle')}
        >
            <YesNoFormGroup
                className="mb-8"
                title={{
                    label: isPartnerSection 
                        ? t('pension-cocktail-fill-screen-supplement-partner-btn-label')
                        : t('pension-cocktail-fill-screen-supplement-btn-label'),
                    tooltip: { title: 'TODO', text: 'TODO' },
                }}
                value={ isPartnerSection
                    ? supplementInfo?.partner?.accountsAvailable === undefined
                        ? null
                        : supplementInfo?.partner?.accountsAvailable
                            ? 'yes'
                            : 'no'  
                    : supplementInfo?.self?.accountsAvailable === undefined
                        ? null
                        : supplementInfo?.self?.accountsAvailable
                            ? 'yes'
                            : 'no'
                }
                onChange={(value) => {
                    if (value === 'yes') {
                        isPartnerSection
                            ? actions.setParameters({ supplementInfo: { partner: { accountsAvailable: true } } })
                            : actions.setParameters({ supplementInfo: { self: { accountsAvailable: true } } });
                    }
                    if (value === 'no') {
                        isPartnerSection
                            ? actions.setParameters({ supplementInfo: { partner: { accountsAvailable: false } } })
                            : actions.setParameters({ supplementInfo: { self: { accountsAvailable: false } } });
                    }
                }}
            />
            {!!supplementInfo?.self?.accountsAvailable && !isPartnerSection && <AccountForms isPartnerSection={false}/>}
            {!!supplementInfo?.partner?.accountsAvailable && isPartnerSection && <AccountForms isPartnerSection={true} />}
            <div className="flex flex-row flex-wrap gap-4 items-start my-8">
                <BackButton />
                <NextButton
                    nextPage={
                        isPartnerSection || !personalInfo?.isCalculatedTogether
                            ? 'results'
                            : 'supplement-partner'}
                    isDisabled={
                        isPartnerSection
                            ? supplementInfo?.partner?.accountsAvailable && !AreAccountsValid(supplementInfo?.partner?.accounts ?? [])
                            : supplementInfo?.self?.accountsAvailable && !AreAccountsValid(supplementInfo?.self?.accounts ?? [])
                    }
                />
            </div>
            <SavingButtons/>
        </CocktailWrapperScreen>
    );
};

const AccountForms: React.FC<{isPartnerSection: boolean} & HTMLProps<HTMLDivElement>> = ({ isPartnerSection, className }) => {
    const {
        actions,
        parameters: { supplementInfo }
    } = useContext(PensionContext);
    
    const getAccounts = () => {
        return (isPartnerSection 
                ? supplementInfo?.partner?.accounts
                : supplementInfo?.self?.accounts)
                ?? []
    }

    const updateAccount = (index:number) => {

        let accounts = ((isPartnerSection 
            ? supplementInfo?.partner?.accounts
            : supplementInfo?.self?.accounts) ?? []);
        
        return (newAccount: Account) => {
            accounts.splice(index, 1, newAccount);
            actions.setParameters(isPartnerSection
                ? { supplementInfo: { partner: { accounts: accounts } } }
                : { supplementInfo: { self: { accounts: accounts } } });
        }
    };

    return (
        <div className={`${className} overflow-y-scroll pr-2`} style={{maxHeight: '52rem'}}>
            {getAccounts().map((acc, i) => 
                <div key={`account-${i}`} className="mb-4">
                    <AccountForm formId={i} updateAccount={updateAccount(i)} currentAccount={acc} isPartnerSection={isPartnerSection} />
                </div>
            )}
            {((isPartnerSection ? supplementInfo?.partner?.accounts : supplementInfo?.self?.accounts) ?? []).length < 10 && 
                <AddAccountButton isPartnerSection={isPartnerSection}/>}
        </div>
    )
}

const AccountForm: React.FC<{
    formId: number,
    isPartnerSection: boolean,
    currentAccount: Account,
    updateAccount: AccountReturnVoid
} & HTMLProps<HTMLDivElement>> = ({ formId, isPartnerSection, className, currentAccount, updateAccount }) => {
    return (
        <div className={className}>
            <span className="font-semibold text-xl mb-8">{`${formId + 1}e rekening`}</span>
            <AccountFormTypeSelect className="mb-8" isPartnerSection={isPartnerSection} updateAccount={updateAccount} currentAccount={currentAccount} />
            <AccountFormAmountInput className="mb-8" isPartnerSection={isPartnerSection} updateAccount={updateAccount} currentAccount={currentAccount} />
            <AccountFormDatePicker className="mb-8" isPartnerSection={isPartnerSection} updateAccount={updateAccount} currentAccount={currentAccount} />
            <RemoveAccountButton className="mb-8" isPartnerSection={isPartnerSection} formId={formId} />
        </div>
    );
};

const AddAccountButton: React.FC<{ isPartnerSection: boolean } & HTMLProps<HTMLDivElement>> 
= ({ isPartnerSection, className }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo },
        actions,
    } = useContext(PensionContext);
    
    return (
        <button
            onClick={() => {
                actions.setParameters(isPartnerSection
                    ? { supplementInfo: { partner: { accounts: (supplementInfo?.partner?.accounts ?? []).concat({ 
                        type: 'saving',
                        recurringPaymentInterval: 'month',
                        riskProfile: 'neutraal'
                    } as Account) } } }
                    : { supplementInfo: { self: { accounts: (supplementInfo?.self?.accounts ?? []).concat({ 
                        type: 'saving',
                        recurringPaymentInterval: 'month',
                        riskProfile: 'neutraal'
                    } as Account) } } });
            }}
            className={`${className} text-bnd-green mb-8`}
        >
            <span>
            {!isPartnerSection && ((supplementInfo?.self?.accounts ?? []).length > 0
                ? t('pension-cocktail-fill-screen-supplement-add-btn')
                : t('pension-cocktail-fill-screen-supplement-add-btn-first'))}
            {isPartnerSection && ((supplementInfo?.partner?.accounts ?? []).length > 0 
                ? t('pension-cocktail-fill-screen-supplement-add-btn')
                : t('pension-cocktail-fill-screen-supplement-add-btn-first'))}
            </span>
        </button>
    );
};

const AccountFormTypeSelect: React.FC<{ updateAccount: AccountReturnVoid, isPartnerSection: boolean, currentAccount: Account } & HTMLProps<HTMLDivElement>> 
= ({ isPartnerSection, currentAccount, className, updateAccount }) => {
    const { t } = useTranslation('pension');

    return (
        <div className={`flex flex-col flex-1 ${className}`}>
            <div className="flex items-center justify-between">
                {t('pension-cocktail-fill-screen-supplement-type-label')}
                <Tooltip tooltipId="id-select-supplement" tooltipTitle="TODO" tooltipText="TODO" className="ml-2">
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            </div>
            <span className="relative flex flex-row items-center pl-4 py-0.5 border rounded-lg bg-white">
                <select
                    className="w-full px-0 py-1.5 pr-10 focus:outline-none appearance-none bg-opacity-0 cursor-pointer"
                    value={currentAccount.type}
                    onChange={(e) => {
                        updateAccount({
                            ...currentAccount,
                            type: e.target.value as AccountType
                        })
                    }}
                >
                    <option value="investment">{t('pension-cocktail-fill-screen-supplement-type-investment')}</option>
                    <option value="saving">{t('pension-cocktail-fill-screen-supplement-type-savings')}</option>
                </select>
                <IconElements.ChevronDown className="absolute right-4 h-4 w-4 text-black pointer-events-none" />
            </span>
        </div>
    );
};

const AccountFormAmountInput: React.FC<{ 
    isPartnerSection: boolean,
    currentAccount: Account,
    updateAccount: AccountReturnVoid,
} & HTMLProps<HTMLDivElement>> = ({ isPartnerSection, currentAccount, className, updateAccount }) => {
    const { t } = useTranslation('pension');
    const [supplementModalShown, setSupplementModalShown] = useState(false);

    return (
        <div className={className}>
            <div className="flex items-center justify-between" style={{ marginBottom: '-24px' }}>
                {t('pension-cocktail-fill-screen-supplement-amount-label')}
                <Tooltip tooltipId={'Todo'} tooltipTitle={'Todo'} tooltipText={'Todo'} className="ml-2">
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            </div>
            <div
                onClick={() => {setSupplementModalShown(true)}}
                className="w-60 relative top-8 float-right flex flex-row justify-end text-bnd-green items-center cursor-pointer pr-4 hover:underline"
            >
                <span>{t('pension-cocktail-fill-screen-supplement-amount-btn')}</span>
                <IconElements.Calculator className="ml-2 text-white h-6 w-6 text-bnd-green" />
            </div>
            <div>
                <CurrencyInput
                    type="text"
                    placeholder="€"
                    className={'bg-white text-black-2 w-full py-2 px-4 border border-gray-200 rounded-lg focus:outline-none'}
                    fixedDecimalLength={0}
                    decimalsLimit={2}
                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                    allowDecimals={false}
                    value={!!currentAccount.predictedAmount ? Math.ceil(currentAccount.predictedAmount / 100) : ''}
                    onValueChange={(value = '', _) => {
                        updateAccount({
                            ...currentAccount,
                            predictedAmount: reverseFormatNumber(value, 'nl-NL') * 100
                        })
                    }}
                />
            </div>
            { supplementModalShown && <SupplementGeneralModal
                isShown={supplementModalShown}
                isPartnerSection={isPartnerSection}
                onClose={() => {setSupplementModalShown(false)}}
                account={currentAccount}
                updateAccount={updateAccount}
            />}
        </div>
    );
};

const AccountFormDatePicker: React.FC<{ updateAccount: AccountReturnVoid, isPartnerSection: boolean, currentAccount: Account } & HTMLProps<HTMLDivElement>> 
= ({ isPartnerSection, currentAccount, className, updateAccount }) => {
    const { t } = useTranslation('pension');

    return (
        <div className={className}>
            <div className="flex items-center justify-between">
                {t('pension-cocktail-fill-screen-supplement-date-label')}
                <Tooltip tooltipId="id-date-supplement" tooltipTitle="TODO" tooltipText="TODO" className="ml-2">
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            </div>
            <div className="relative mb-20">
                <input
                    className="absolute text-black-2 w-full py-2 px-4 border rounded-lg focus:outline-none bg-white border-gray-200"
                    placeholder="dd-mm-yyyy"
                    type="Date"
                    required
                    value={currentAccount.availableAt ?? ''}
                    onChange={(e) => {
                        updateAccount({
                            ...currentAccount,
                            availableAt: e.target.value
                        })
                    }}
                ></input>
                <IconElements.Calendar className="absolute top-2 right-4" />
            </div>
        </div>
    );
};

const RemoveAccountButton: React.FC<{ formId: number, isPartnerSection: boolean } & HTMLProps<HTMLDivElement>> 
= ({ formId, isPartnerSection, className }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo },
        actions,
    } = useContext(PensionContext);    
    const [formShown, setFormShown] = useState(false);

    const removeAccount = (index:number) => {
        let accounts = ((isPartnerSection 
            ? supplementInfo?.partner?.accounts
            : supplementInfo?.self?.accounts) ?? []);
        accounts.splice(index, 1);
        actions.setParameters(isPartnerSection
            ? { supplementInfo: {partner: { accounts: accounts } } }
            : { supplementInfo: {self: { accounts: accounts } } });
    };

    return (
        <>
            <button 
                className={`${className} text-bnd-green`}
                onClick={() => {setFormShown(true)}}>
                {t("pension-cocktail-fill-screen-supplement-remove-btn")}
            </button>
            <RemoveAccountModal
                formId={formId}
                onRemoveAccount={removeAccount}
                isShown={formShown}
                onClose={() => {
                    setFormShown(false);
                }}
            /> 
        </>
    )
}

function AreAccountsValid(accounts: Account[]): boolean {
    return accounts.every(isAccountValid);
}

function isAccountValid(account: Account): boolean {
    return !!account.availableAt && !!account.predictedAmount;
}
